import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Wind, Activity, Sparkles, Droplet, Save, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const metricFields = [
  { 
    key: 'heart_rate', 
    icon: Heart, 
    label: 'Heart Rate', 
    unit: 'bpm', 
    color: '#D54B48',
    min: 40, max: 200, default: 72,
    description: 'Resting heart rate'
  },
  { 
    key: 'breathing_rate', 
    icon: Wind, 
    label: 'Breathing Rate', 
    unit: 'rpm', 
    color: '#8BB89F',
    min: 8, max: 30, default: 16,
    description: 'Breaths per minute'
  },
  { 
    key: 'hrv', 
    icon: Activity, 
    label: 'HRV (SDNN)', 
    unit: 'ms', 
    color: '#F8B64C',
    min: 10, max: 100, default: 45,
    description: 'Heart rate variability'
  },
  { 
    key: 'spo2', 
    icon: Droplet, 
    label: 'SpO₂', 
    unit: '%', 
    color: '#6D2A83',
    min: 85, max: 100, default: 98,
    description: 'Blood oxygen saturation'
  },
  { 
    key: 'stress_index', 
    icon: Sparkles, 
    label: 'Stress Index', 
    unit: '%', 
    color: '#C10891',
    min: 0, max: 100, default: 35,
    description: 'Perceived stress level'
  }
];

export default function ManualEntryPage() {
  const navigate = useNavigate();
  const [values, setValues] = useState(
    metricFields.reduce((acc, field) => ({ ...acc, [field.key]: field.default }), {})
  );
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  const handleValueChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const metrics = {};
      metricFields.forEach(field => {
        metrics[field.key] = {
          value: values[field.key],
          confidence: 1.0, // Manual entry = 100% confidence
          source: 'manual'
        };
      });

      await axios.post(`${API}/biometrics/sync`, {
        scan_type: 'manual_entry',
        metrics,
        notes,
        timestamp: new Date().toISOString()
      });

      toast.success('Vitals saved! Twin Score will refresh soon.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Manual Entry</h1>
              <p className="text-xs text-muted-foreground">Enter your vitals manually</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-4 border-l-4 border-l-[#8BB89F]">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-[#8BB89F] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm">
                  Enter values from another device or measurement. Data will be marked as manually entered.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Metric Inputs */}
        <div className="space-y-4">
          {metricFields.map((field, index) => {
            const Icon = field.icon;
            return (
              <motion.div
                key={field.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="p-2 rounded-xl"
                      style={{ backgroundColor: `${field.color}20` }}
                    >
                      <Icon size={18} style={{ color: field.color }} />
                    </div>
                    <div className="flex-1">
                      <Label className="font-medium">{field.label}</Label>
                      <p className="text-xs text-muted-foreground">{field.description}</p>
                    </div>
                    <div className="text-right">
                      <span 
                        className="text-2xl font-bold font-heading"
                        style={{ color: field.color }}
                      >
                        {values[field.key]}
                      </span>
                      <span className="text-xs text-muted-foreground ml-1">{field.unit}</span>
                    </div>
                  </div>
                  <Slider
                    value={[values[field.key]]}
                    onValueChange={([v]) => handleValueChange(field.key, v)}
                    min={field.min}
                    max={field.max}
                    step={1}
                    className="mt-2"
                    data-testid={`slider-${field.key}`}
                  />
                  <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                    <span>{field.min}</span>
                    <span>{field.max}</span>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="glass-card p-4">
            <Label className="font-medium mb-2 block">Notes (optional)</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Measured after morning workout"
              className="w-full h-20 bg-transparent border border-border rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="notes-input"
            />
          </Card>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#8BB89F] hover:bg-[#8BB89F]/90 rounded-full py-6 text-lg"
            data-testid="save-manual-btn"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save to Twin'}
          </Button>
        </motion.div>
      </main>

      <BottomNav />
    </div>
  );
}
